<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, watch } from "vue"
import { useRoute, useRouter } from "vue-router"
import { storeToRefs } from "pinia"
import { useAuthStore } from "@core/stores/auth.store"
import { useHomeWorkspaceStore } from "@modules/home/stores/homeStore.store"

// ================================
// TYPES & INTERFACES
// ================================
interface Props {
    disabledSearch?: boolean
    disabledRemove?: boolean
    disabledMove?: boolean
    searchMode?: boolean
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    disabledSearch: false,
    disabledRemove: false,
    disabledMove: false,
    searchMode: false,
})

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const homeWorkspace = useHomeWorkspaceStore()
const { folders, loadingFolders } = storeToRefs(homeWorkspace)

/** Tri dossiers (API) — distinct de `folderBuildingSort` pour les bâtiments. */
const FOLDER_SORT_OPTIONS = [
    { label: "Nom", value: "name" },
    { label: "Date de création", value: "createdAt" },
] as const

const FOLDER_ORDER_OPTIONS = [
    { label: "Croissant", value: "asc" },
    { label: "Décroissant", value: "desc" },
] as const

// ================================
// STATE
// ================================
/** Nombre de lignes squelette pendant le chargement (même grille / gabarit qu’un dossier). */
const FOLDER_LIST_LOADING_SKELETON_COUNT = 6

// ================================
// COMPUTED
// ================================
const getFolderSortValue = computed({
    get() {
        return (route.query.folderSort as string) ?? "createdAt"
    },
    set(v: string) {
        router.replace({
            query: { ...route.query, folderSort: v },
        })
    },
})

const getFolderOrderValue = computed({
    get() {
        return (route.query.folderOrder as string) ?? "desc"
    },
    set(v: string) {
        router.replace({
            query: { ...route.query, folderOrder: v },
        })
    },
})

/** Identifiants pour le watch (primitifs) — évite un refetch dossiers quand seul le tri bâtiments change. */
const getFolderWatchFolderId = computed(
    () => (route.params.folderId as string) || null
)
const getFolderWatchSearch = computed(() =>
    props.searchMode ? (route.query.search as string) || null : null
)
const getFolderWatchSort = computed(
    () => (route.query.folderSort as string) ?? "createdAt"
)
const getFolderWatchOrder = computed(
    () => (route.query.folderOrder as string) ?? "desc"
)

// ================================
// INTERACTIONS
// ================================
/**
 * Ouvre le dialogue de création d’un dossier dans le dossier courant (équivalent legacy « Nouveau dossier »).
 */
function onOpenCreateFolder(): void {
    homeWorkspace.openModal("folderCreate", {})
}

// ================================
// WATCHERS
// ================================
watch(
    [
        getFolderWatchFolderId,
        getFolderWatchSearch,
        getFolderWatchSort,
        getFolderWatchOrder,
    ],
    ([folderId, search, sort, order]) => {
        homeWorkspace.currentFolderId = folderId
        void homeWorkspace.fetchFoldersPage({
            folderId,
            reset: true,
            search: search ?? undefined,
            sort,
            order: order === "desc" ? "desc" : "asc",
        })
    },
    { immediate: true }
)
</script>

<template>
    <!-- ================================ -->
    <!-- LISTE DOSSIERS -->
    <!-- ================================ -->
    <section
        class="dashboard-folder-list flex flex-col gap-4"
        data-cy="dashboard-folder-list"
    >
        <div class="flex flex-wrap items-end gap-3">
            <div class="flex flex-col gap-1">
                <label class="text-sm text-surface-600">Tri dossiers</label>
                <BaseSelect
                    v-model="getFolderSortValue"
                    :options="[...FOLDER_SORT_OPTIONS]"
                    option-label="label"
                    option-value="value"
                    class="min-w-40"
                />
            </div>
            <div class="flex flex-col gap-1">
                <label class="text-sm text-surface-600">Ordre</label>
                <BaseSelect
                    v-model="getFolderOrderValue"
                    :options="[...FOLDER_ORDER_OPTIONS]"
                    option-label="label"
                    option-value="value"
                    class="min-w-36"
                />
            </div>
        </div>

        <ul
            v-if="loadingFolders"
            class="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 lg:grid-cols-3!"
            aria-busy="true"
            aria-label="Chargement des dossiers"
        >
            <li
                v-for="n in FOLDER_LIST_LOADING_SKELETON_COUNT"
                :key="`folder-skeleton-${n}`"
                class="list-none"
            >
                <BaseSkeleton
                    class="flex w-full min-w-0 px-3.5 py-3"
                    height="4rem"
                >
                </BaseSkeleton>
            </li>
        </ul>

        <ul
            v-else
            class="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4 lg:grid-cols-3!"
        >
            <HomeFolderRow
                v-for="folder in folders"
                :key="folder._id"
                :folder="folder"
                :disabled-move="props.disabledMove"
                :disabled-remove="props.disabledRemove"
            />
            <li
                v-if="
                    !props.searchMode &&
                    authStore.isEmployeWriteOrHigher &&
                    !loadingFolders
                "
                class="list-none"
                data-cy="dashboard-folder-add-row"
            >
                <button
                    type="button"
                    class="box-border flex h-[4rem] min-h-[4rem] max-h-[4rem] w-full min-w-0 items-center gap-2 rounded-lg border border-dashed border-surface-300 bg-surface-50 px-3.5 text-left font-medium text-surface-700 shadow-sm transition-colors hover:border-primary hover:bg-primary-50/40 hover:text-primary cursor-pointer"
                    @click="onOpenCreateFolder"
                >
                    <i
                        class="pi pi-folder-plus shrink-0 text-surface-500"
                        aria-hidden="true"
                    />
                    <span>Nouveau dossier</span>
                </button>
            </li>
        </ul>
    </section>
</template>
